import { m } from "@nerves/i18n/messages";
import { Robot } from "@phosphor-icons/react";
import type { CSSProperties } from "react";
import { cx } from "@/lib/cx";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

/**
 * Initials stay at 10px or more: the two smallest circles show the first letter only, at 10px (xs)
 * and 11px (sm), instead of two letters at 8 to 9px.
 */
const box: Record<AvatarSize, string> = {
  xs: "size-4 text-[10px]",
  sm: "size-5 text-[11px]",
  md: "size-6 text-[10px]",
  lg: "size-8 text-xs",
  xl: "size-10 text-sm",
};
const SINGLE_INITIAL: Record<AvatarSize, boolean> = { xs: true, sm: true, md: false, lg: false, xl: false };
const icon: Record<AvatarSize, number> = { xs: 10, sm: 12, md: 14, lg: 18, xl: 22 };

function initials(name: string, single: boolean) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  if (single) return first.toUpperCase();
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/** Stable 1..8 from any string (FNV-1a). Same seed, same tint, in every view and session. */
export function avatarHue(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return ((h >>> 0) % 8) + 1;
}

export interface AvatarProps {
  name: string;
  /** Stable identity for the colour (user id). Falls back to the name. */
  seed?: string;
  src?: string | null;
  size?: AvatarSize;
  /** Agents (Claude via MCP) get a rounded square with a robot glyph: they differ by shape, not only colour. */
  bot?: boolean;
  /** Pass false when the name is rendered next to the avatar (decorative). */
  labelled?: boolean;
  className?: string;
}

/**
 * People are circles tinted from one of 8 low-chroma hues (tokens --avatar-N-bg/fg, verified >= 6.4:1).
 * Bots are rounded squares in the neutral surface with the robot glyph.
 */
export function Avatar({ name, seed, src, size = "md", bot, labelled = true, className }: AvatarProps) {
  const label = bot ? `${name} (${m.avatar_agent()})` : name;
  const hue = avatarHue(seed ?? name);
  const tint = bot
    ? undefined
    : ({ backgroundColor: `var(--avatar-${hue}-bg)`, color: `var(--avatar-${hue}-fg)` } as CSSProperties);
  return (
    <span
      {...(labelled ? { role: "img", "aria-label": label, title: label } : { "aria-hidden": true })}
      style={tint}
      className={cx(
        "relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden font-semibold leading-none tracking-[0.02em]",
        box[size],
        bot
          ? "rounded-mark border border-line bg-active text-fg-muted"
          : "rounded-full shadow-[inset_0_0_0_1px_var(--shadow-edge)]",
        className,
      )}
    >
      {bot ? (
        <Robot size={icon[size]} weight="duotone" aria-hidden="true" />
      ) : src ? (
        <img src={src} alt="" className="size-full object-cover" />
      ) : (
        <span aria-hidden="true">{initials(name, SINGLE_INITIAL[size])}</span>
      )}
    </span>
  );
}
